import React, { ChangeEvent, useState } from 'react';
import { DataTable } from '@/components/shared';
import { List } from '@refinedev/antd';
import { endPoints } from '@/api/configs';
import { useList } from '@/hooks/useList';
import { useTranslation } from '@refinedev/core';
import { PlusOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import { getColumns } from './columns';
import { Form, Input, Select, Button, Space, message } from 'antd';
import { UserData } from '@/types/user';
import {
  BulkAddUsersModal,
  AddUserModal,
  BulkAddAchievementsModal,
} from '@/components/modals';
import useAPI from '@/api/useApi';

export const UserList = () => {
  const { translate } = useTranslation();
  const [deactivateId, setDeactivateId] = useState<string>('');

  const [form] = Form.useForm();
  const {
    isLoading,
    page,
    setPage,
    isRefetching,
    setSearch,
    setFilters,
    refetch,
    data,
    resolvedTotal,
  } = useList<{
    data: UserData[];
  }>({
    endpoint: endPoints.listingUsers,
    filters: {
      status: undefined,
    },
  });
  const { usePost } = useAPI();
  const { mutate: deactivateUser, isLoading: isLoadingDeactivate } = usePost(
    endPoints.toggleActivation(deactivateId)
  );
  const handleDeactivate = (is_active: boolean) => {
    deactivateUser(
      { is_active },
      {
        onSuccess: () => {
          message.success(
            !is_active
              ? translate('messages.success-activate')
              : translate('messages.success-deactivate')
          );
          refetch();
        },
        onError: (error) => {
          message.error(error.message);
        },
      }
    );
  };
  const columns = getColumns(
    translate,
    isLoadingDeactivate,
    handleDeactivate,
    setDeactivateId,
    deactivateId
  );
  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    setSearch(e.target.value);
  };
  const onIsActiveChange = (value: boolean) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      is_active: value,
    }));
  };
  const onGenderChange = (value: string) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      gender: value,
    }));
  };
  const onStatusChange = (value: string) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      status: value,
    }));
  };
  const resetFilters = () => {
    form.resetFields();
    setFilters({
      status: undefined,
      is_active: undefined,
      gender: undefined,
    });
  };
  const [isBulkAddModalOpen, setIsBulkAddModalOpen] = React.useState(false);
  const [handleBulkAchievements, setHandleBulkAchievements] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const handleBulkCreate = (type: 'users' | 'achievements') => {
    if (type === 'users') {
      setIsBulkAddModalOpen(true);
    } else {
      setHandleBulkAchievements(true);
    }
  };
  return (
    <List
      title={
        <div className="flex gap-x-3">
          <UserOutlined />
          <h1>{translate('listing-users.title')}</h1>
        </div>
      }
      canCreate={false}
      headerButtons={
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleBulkCreate('users')}
          >
            {translate('buttons.bulk-create')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleBulkCreate('achievements')}
          >
            {translate('buttons.bulk-add-achievements')}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <div className="flex flex-wrap gap-x-3">
          <Form.Item name="search" className="w-full sm:w-1/6 min-w-[200px]">
            <Input
              placeholder={translate('search-user.fields.search')}
              onChange={onSearch}
            />
          </Form.Item>
          <Form.Item name="is_active" className="w-full sm:w-1/6 min-w-[200px]">
            <Select
              placeholder={translate('search-user.fields.status')}
              onChange={onIsActiveChange}
              allowClear
              options={[
                { label: translate('status.active'), value: true },
                { label: translate('status.inactive'), value: false },
              ]}
            />
          </Form.Item>
          <Form.Item name="status" className="w-full sm:w-1/6 min-w-[200px]">
            <Select
              placeholder={translate('search-user.fields.status')}
              onChange={onStatusChange}
              allowClear
              options={[
                { label: translate('listing-users.pending'), value: 'PENDING' },
                {
                  label: translate('listing-users.approved'),
                  value: 'APPROVED',
                },
                {
                  label: translate('listing-users.rejected'),
                  value: 'REJECTED',
                },
              ]}
            />
          </Form.Item>
          <Form.Item name="gender" className="w-full sm:w-1/6 min-w-[200px]">
            <Select
              placeholder={translate('search-user.fields.gender')}
              onChange={onGenderChange}
              allowClear
              options={[
                {
                  label: translate('listing-users.gender.MALE'),
                  value: 'MALE',
                },
                {
                  label: translate('listing-users.gender.FEMALE'),
                  value: 'FEMALE',
                },
              ]}
            />
          </Form.Item>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={resetFilters}
          />
        </div>
      </Form>
      <DataTable
        pageSize={10}
        currentPage={page}
        onPageChange={setPage}
        columns={columns}
        loading={isLoading || isRefetching}
        data={{
          data: data?.data.data ? data.data.data : [],
          headers: {
            'x-total-count': resolvedTotal,
          },
        }}
      />
      <BulkAddUsersModal
        isOpen={isBulkAddModalOpen}
        onClose={() => setIsBulkAddModalOpen(false)}
        onSuccess={() => {
          refetch();
        }}
      />
      <BulkAddAchievementsModal
        isOpen={handleBulkAchievements}
        onClose={() => setHandleBulkAchievements(false)}
        onSuccess={() => {
          refetch();
        }}
      />
      <AddUserModal
        open={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
      />
    </List>
  );
};
